import { useEffect, useState, useCallback } from "react";
import { Hand, Users, MicOff, X, UserPlus, ShieldCheck, Shield } from "lucide-react";
import { toast } from "sonner";
import { plyxApi as api, getPlyxToken } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { primePlyxMediaPermissions, plyxMediaPermissionMessage } from "@/lib/plyxMediaPermissions";

/**
 * PlyxStagePanel — co-host management UI for a Plyx Live room.
 *
 * One component, three roles:
 *   • **Host** + **moderators** see the full panel: hand-raise queue,
 *     pending invites, current guests with Mute / Remove buttons,
 *     and (host-only) "Make moderator" toggles.
 *   • **Viewers** see only a single "✋ Raise hand" button so they
 *     can request the stage without bloating their UI.
 *
 * State pulls from `GET /plyx/live/{room}/stage` (snapshot). For
 * real-time updates we listen for `live.stage.*` events on the same
 * `/api/ws/plyx/user` socket the ripple inbox already keeps open —
 * one WS, many use cases, zero new connections.
 *
 * When the host invites the local viewer (or approves their hand-
 * raise) we surface a toast with Accept / Decline. Accept calls back
 * via the `onAcceptInvite` prop so the parent (PlyxLiveViewer) can
 * tear down its subscriber LiveKitRoom and reconnect with the new
 * publisher token. That's the only piece of host-side LiveKit
 * integration this component delegates to the parent.
 */

const PlyxStagePanel = ({ roomName, hostId, isBroadcaster, onAcceptInvite }) => {
  const { user } = usePlyxAuth();
  const [stage, setStage] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const userId = user?.plyx_user_id;
  const isHost = isBroadcaster || userId === hostId;
  const isMod = isHost || (stage?.moderators || []).includes(userId);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/plyx/live/${roomName}/stage`);
      setStage(r.data);
    } catch {
      /* viewer may not have read access if stream just ended */
    }
  }, [roomName]);

  // ── Initial + periodic refresh as a safety net for WS misses ─────
  useEffect(() => {
    if (!user) return undefined;
    refresh();
    const id = setInterval(refresh, 8000);
    return () => clearInterval(id);
  }, [user, refresh]);

  // ── Live stage events via the personal-inbox WebSocket ───────────
  // We open our own WS here (not relying on the global ripples
  // listener) because we want the stage events to drive UI updates
  // even when /plyx/inbox isn't mounted.
  useEffect(() => {
    if (!user) return undefined;
    const token = getPlyxToken();
    if (!token) return undefined;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const ws = new WebSocket(
      `${backend.replace(/^http/, "ws")}/api/ws/plyx/user?token=${encodeURIComponent(token)}`,
    );
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (!msg?.type?.startsWith?.("live.stage")) return;
      if (msg.room_name && msg.room_name !== roomName) return;

      if (msg.type === "live.stage.invite") {
        // Pop a toast with Accept / Decline.
        toast(`${msg.from_name || "@" + (msg.from_handle || "host")} invited you on stage`, {
          duration: 20000,
          action: {
            label: "Accept",
            onClick: async () => {
              // iOS Safari needs getUserMedia to fire INSIDE the tap
              // handler (synchronous user gesture) — once we await the
              // backend the gesture context is lost and the perm prompt
              // never appears. Prime here first.
              const perm = await primePlyxMediaPermissions({ video: true, audio: true });
              if (!perm.ok) {
                toast.error(plyxMediaPermissionMessage(perm.error));
                return;
              }
              try {
                const r = await api.post(`/plyx/live/${roomName}/invite/accept`);
                onAcceptInvite?.(r.data);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Couldn't join — try again");
              }
            },
          },
          cancel: {
            label: "Decline",
            onClick: () => {
              api.post(`/plyx/live/${roomName}/invite/decline`).catch(() => {});
            },
          },
        });
      } else if (msg.type === "live.stage.removed") {
        toast.warning("The host removed you from the stage");
      } else if (msg.type === "live.stage.muted") {
        toast.info(msg.muted ? "You've been muted by the host" : "You've been unmuted");
      } else if (msg.type === "live.stage.moderator") {
        toast.success(msg.promoted ? "You're now a moderator ✨" : "You're no longer a moderator");
        refresh();
      } else if (msg.type === "live.stage.update") {
        if (msg.stage) setStage(msg.stage);
      }
    };
    return () => { try { ws.close(); } catch { /* ignore */ } };
  }, [user, roomName, onAcceptInvite, refresh]);

  if (!user) return null;

  const guestCount = (stage?.guests || []).length;
  const max = stage?.max_guests || 10;
  const stageFull = guestCount >= max;
  const youOnStage = (stage?.guests || []).includes(userId);

  // ── Viewer-only compact button — raise hand ──────────────────────
  if (!isMod) {
    if (youOnStage) return null;  // already on stage, no CTA needed
    return (
      <button
        type="button"
        onClick={async () => {
          if (busy) return;
          setBusy(true);
          try {
            await api.post(`/plyx/live/${roomName}/request-to-speak`);
            toast.success("Hand raised — host will be notified");
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't raise hand");
          } finally {
            setBusy(false);
          }
        }}
        disabled={busy}
        data-testid="plyx-stage-raise-hand"
        className="absolute bottom-24 left-3 z-[55] inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-300 text-black font-semibold text-xs shadow-lg shadow-amber-900/40 hover:bg-amber-200 transition disabled:opacity-60"
      >
        <Hand className="w-4 h-4" />
        Raise hand
      </button>
    );
  }

  // ── Host / moderator panel ───────────────────────────────────────
  const handRaises = stage?.hand_raises || [];
  const pendingInvites = stage?.pending_invites || [];
  const guests = stage?.guests || [];

  const act = async (fn) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); await refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="plyx-stage-panel-toggle"
        className={
          "absolute top-20 left-3 z-[55] inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full font-bold text-xs shadow-xl transition "
          + (handRaises.length > 0
            ? "bg-gradient-to-r from-amber-300 to-amber-500 text-black animate-pulse shadow-amber-900/40"
            : "bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black shadow-black/40")
        }
        aria-label="Open stage panel — invite or remove guests"
      >
        <UserPlus className="w-4 h-4" />
        <span>Invite to stage</span>
        <span className="ml-1 px-1.5 py-0.5 rounded-full bg-black/30 text-white text-[10px] font-black">
          {guestCount}/{max}
        </span>
        {handRaises.length > 0 && (
          <span
            className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-black text-amber-300 text-[10px] font-black"
            data-testid="plyx-stage-handraise-count"
          >
            ✋{handRaises.length}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute top-32 left-3 z-[55] w-[min(360px,calc(100vw-1.5rem))] max-h-[60vh] overflow-y-auto rounded-2xl bg-[#13102a] text-white border border-white/10 shadow-2xl shadow-black/50"
          data-testid="plyx-stage-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 bg-[#13102a]">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-300" />
              <span className="text-sm font-semibold">Stage</span>
              <span className="text-[11px] text-white/50">{guestCount}/{max} on stage</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="plyx-stage-panel-close"
              className="p-1 rounded-full hover:bg-white/5"
              aria-label="Close stage panel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Hand raises queue */}
          {handRaises.length > 0 && (
            <Section title={`✋ Requests (${handRaises.length})`}>
              {handRaises.map((id) => (
                <Row key={id} userId={id}>
                  <button
                    type="button"
                    onClick={() => act(() => api.post(`/plyx/live/${roomName}/requests/${id}/approve`))}
                    disabled={stageFull}
                    data-testid={`plyx-stage-approve-${id}`}
                    className="px-3 py-1 rounded-full bg-emerald-500 text-white text-[11px] font-semibold disabled:opacity-50"
                  >Approve</button>
                  <button
                    type="button"
                    onClick={() => act(() => api.post(`/plyx/live/${roomName}/requests/${id}/dismiss`))}
                    data-testid={`plyx-stage-dismiss-${id}`}
                    className="px-3 py-1 rounded-full bg-white/10 text-white/70 text-[11px]"
                  >Dismiss</button>
                </Row>
              ))}
            </Section>
          )}

          {/* Pending invites */}
          {pendingInvites.length > 0 && (
            <Section title={`Pending invites (${pendingInvites.length})`}>
              {pendingInvites.map((id) => (
                <Row key={id} userId={id}>
                  <span className="text-[11px] text-white/45 italic">awaiting…</span>
                </Row>
              ))}
            </Section>
          )}

          {/* Current guests */}
          <Section title={`On stage (${guestCount})`}>
            {guestCount === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-white/50">
                No guests yet. Tap a hand-raise above to bring someone up, or invite directly from chat.
              </div>
            ) : (
              guests.map((id) => {
                const userIsMod = (stage?.moderators || []).includes(id);
                return (
                  <Row key={id} userId={id} badge={userIsMod ? "MOD" : null}>
                    <button
                      type="button"
                      onClick={() => act(() => api.post(`/plyx/live/${roomName}/guests/${id}/mute`))}
                      data-testid={`plyx-stage-mute-${id}`}
                      className="p-1.5 rounded-full hover:bg-white/10 text-white/70"
                      title="Mute"
                    >
                      <MicOff className="w-4 h-4" />
                    </button>
                    {isHost && (
                      <button
                        type="button"
                        onClick={() => act(() => (
                          userIsMod
                            ? api.delete(`/plyx/live/${roomName}/moderators/${id}`)
                            : api.post(`/plyx/live/${roomName}/moderators/${id}`)
                        ))}
                        data-testid={`plyx-stage-mod-${id}`}
                        className={
                          "p-1.5 rounded-full hover:bg-white/10 transition "
                          + (userIsMod ? "text-amber-300" : "text-white/70")
                        }
                        title={userIsMod ? "Remove moderator" : "Make moderator"}
                      >
                        {userIsMod ? <ShieldCheck className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => act(() => api.post(`/plyx/live/${roomName}/guests/${id}/remove`))}
                      data-testid={`plyx-stage-remove-${id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-300 text-[11px] font-semibold"
                      title="Remove from stage"
                    >
                      <X className="w-3.5 h-3.5" />
                      Remove
                    </button>
                  </Row>
                );
              })
            )}
          </Section>

          {/* Invite a registered member by @handle. Searches plyx_users
              and lets the host pull anyone up without leaving the panel. */}
          <InviteByHandleRow roomName={roomName} onInvited={refresh} />

          {/* Hint for inviting by handle — uses chat as the discovery
              surface, no new search UI to maintain. */}
          <div className="px-4 py-3 text-[11px] text-white/45 border-t border-white/10">
            <UserPlus className="w-3 h-3 inline mr-1.5 -mt-0.5" />
            Or tap any viewer&apos;s name in chat → &ldquo;Invite to stage&rdquo;.
          </div>
        </div>
      )}
    </>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-white/40 border-b border-white/5">
      {title}
    </div>
    <div>{children}</div>
  </div>
);

const Row = ({ userId, badge, children }) => {
  return (
    <div className="px-4 py-2.5 flex items-center gap-2 border-b border-white/5 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-mono truncate">{userId}</p>
      </div>
      {badge && (
        <span className="text-[10px] font-black tracking-wider px-1.5 py-0.5 rounded bg-amber-300 text-black">
          {badge}
        </span>
      )}
      <div className="flex items-center gap-1 shrink-0">{children}</div>
    </div>
  );
};

/**
 * InviteByHandleRow — debounced @handle / name search that lets the host
 * pull any registered Plyx member onto the stage. Hits /plyx/users/search
 * which returns prefix-matched users; tapping a row fires the existing
 * /invite-guest endpoint so the rest of the flow (notification, accept,
 * token swap) is shared with the chat-side invite path.
 */
const InviteByHandleRow = ({ roomName, onInvited }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = q.trim();
    if (!t) { setResults([]); return undefined; }
    const id = setTimeout(async () => {
      try {
        const r = await api.get(`/plyx/users/search?q=${encodeURIComponent(t)}&limit=8`);
        setResults(r.data?.users || []);
      } catch { setResults([]); }
    }, 220);
    return () => clearTimeout(id);
  }, [q]);

  const invite = async (uid, label) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/plyx/live/${roomName}/invite-guest`, { user_id: uid });
      toast.success(`Invite sent to ${label}`);
      setQ(""); setResults([]);
      onInvited?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not invite");
    } finally { setBusy(false); }
  };

  return (
    <div className="px-4 py-3 border-t border-white/10">
      <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mb-2">Invite anyone</p>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search @handle or name…"
        data-testid="plyx-stage-invite-search"
        className="w-full rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-300"
      />
      {results.length > 0 && (
        <div className="mt-2 rounded-xl bg-black/40 border border-white/5 overflow-hidden">
          {results.map((u) => (
            <button
              key={u.plyx_user_id}
              type="button"
              onClick={() => invite(u.plyx_user_id, `@${u.handle}`)}
              disabled={busy}
              data-testid={`plyx-stage-invite-result-${u.handle}`}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 text-left transition disabled:opacity-50"
            >
              {u.avatar_url ? (
                <img src={u.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-300 to-pink-400" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-white truncate">{u.display_name || `@${u.handle}`}</p>
                <p className="text-[10px] text-white/40 truncate">@{u.handle}</p>
              </div>
              <UserPlus className="w-3.5 h-3.5 text-amber-300" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlyxStagePanel;
